import throttle from 'lodash/throttle'
import NielsenDataRetriever from './NielsenDataRetriever'
import { MmsNielsenTrackingData } from './MMSNielsen'

export enum StreamingAnalyticsPlayerEvent {
  POSITION = 'POSITION',
  STOP = 'STOP',
  END = 'END',
}

export enum NielsenEvent {
  POSITION = 'setPlayheadPosition',
  STOP = 'stop',
  END = 'end',
}

export interface NSdkInstance {
  ggPM: (
    event: string,
    position: number | Record<string, string | number>,
  ) => void
}

class NielsenEventHandler {
  private readonly data: NielsenDataRetriever

  private readonly nSdkInstance: NSdkInstance | undefined

  private readonly contentMetadataObject: Record<string, string | number>

  private previousPosition = -1

  private hasSentMetadata = false

  constructor(
    private readonly jwPlayer: jwplayer.JWPlayer & JWPlayerStatic,
    private readonly currentProduct: MmsNielsenTrackingData,
  ) {
    this.data = new NielsenDataRetriever(this.jwPlayer, this.currentProduct)
    const { NOLBUNDLE } = window

    if (this.data.APP_ID && NOLBUNDLE) {
      this.nSdkInstance = NOLBUNDLE.nlsQ(this.data.APP_ID, 'nlsnInstance', {
        // nol_sdkDebug: 'debug',
      })
    }

    this.contentMetadataObject = {
      type: 'content',
      assetid: this.data.getEpisodeId(),
      length: this.data.getDuration(),
      program: this.data.getProgramTitle(),
      title: this.data.getEpisodeTitle(),
      ispremiumcontent: this.data.PREMIUM_CONTENT_FLAG,
      isautoplay: this.data.getAutostartedFlag(),
      plugv: this.data.getJwPlayerVersion(),
      playerv: this.data.MEDIA_PLAYER,
    }
  }

  sendEvent = (
    event: StreamingAnalyticsPlayerEvent,
    position: number = this.data.getPosition(),
  ) => {
    if (!this.nSdkInstance) return

    switch (event) {
      case StreamingAnalyticsPlayerEvent.POSITION:
        this.nSdkInstance.ggPM(NielsenEvent.POSITION, position)
        break
      case StreamingAnalyticsPlayerEvent.STOP:
        this.nSdkInstance.ggPM(NielsenEvent.STOP, position)
        break
      case StreamingAnalyticsPlayerEvent.END:
        if (this.hasSentMetadata)
          this.nSdkInstance.ggPM(NielsenEvent.END, position)
        break
      default:
    }
  }

  onTime = (event: { position: number }) => {
    if (this.hasSentMetadata) this.throttlePositionEvent(event.position)
  }

  onFirstFrame = (_event: unknown) => {
    this.loadMetadataEvent()
  }

  private loadMetadataEvent() {
    if (!this.nSdkInstance) return

    this.nSdkInstance.ggPM('loadMetadata', this.contentMetadataObject)
    this.hasSentMetadata = true
  }

  private positionEvent(position: number) {
    const currentPosition = Math.round(position)

    if (currentPosition !== this.previousPosition) {
      this.sendEvent(StreamingAnalyticsPlayerEvent.POSITION, currentPosition)
    }

    this.previousPosition = currentPosition
  }

  private readonly POSITION_EVENT_THROTTLE_TIME = 900

  private readonly throttlePositionEvent = throttle(
    this.positionEvent,
    this.POSITION_EVENT_THROTTLE_TIME,
  )

  onComplete = () => {
    this.sendEvent(StreamingAnalyticsPlayerEvent.END)
    this.resetLoadMetadata()
  }

  private resetLoadMetadata() {
    this.hasSentMetadata = false
  }

  onUnload = () => {
    this.sendEvent(StreamingAnalyticsPlayerEvent.END)
  }
}

export default NielsenEventHandler
